import React, { useState, useCallback, useEffect } from "react";
import "@/App.css";
import { LanguageProvider } from "./context/LanguageContext";
import { CartProvider } from "./context/CartContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { ScrollProvider } from "./context/ScrollContext";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import CategoryTabs from "./components/CategoryTabs";
import SearchBar from "./components/SearchBar";
import KidsMenuHighlight from "./components/KidsMenuHighlight";
import FoodSafetyNotice from "./components/FoodSafetyNotice";
import MenuSection from "./components/MenuSection";
import ProductModal from "./components/ProductModal";
import CartDrawer from "./components/CartDrawer";
import FloatingButtons from "./components/FloatingButtons";
import Footer from "./components/Footer";

const MenuPage = () => {
  const { isDark } = useTheme();
  
  // ⭐ ВЗЕМАМЕ ЗАПАЗЕНАТА КАТЕГОРИЯ ПРИ ПЪРВО ЗАРЕЖДАНЕ ⭐
  const getInitialCategory = () => {
    const saved = sessionStorage.getItem('activeCategory');
    return saved || 'pizza';
  };

  const [activeCategory, setActiveCategory] = useState(getInitialCategory);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ⭐ ЗАПАЗВАМЕ КАТЕГОРИЯТА ПРИ ПРОМЯНА ⭐
  useEffect(() => {
    sessionStorage.setItem('activeCategory', activeCategory);
  }, [activeCategory]);

  const scrollToMenu = useCallback(() => {
    const menuContainer = document.getElementById('menu-container');
    if (menuContainer) {
      const offset = 100;
      const elementPosition = menuContainer.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }, []);

  const handleNavigate = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    // Запазваме в sessionStorage
    sessionStorage.setItem('activeCategory', categoryId);
    
    const menuContainer = document.getElementById('menu-container');
    if (menuContainer) {
      const offset = 100;
      const elementPosition = menuContainer.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }, []);

  const handleOpenModal = useCallback((product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProduct(null), 300);
  }, []);

  return (
    <div 
      className={`min-h-screen transition-colors duration-300 ${
        isDark ? 'bg-[#0A0A0A]' : 'bg-white'
      }`} 
      data-testid="menu-page"
    >
      <Navbar activeSection={activeCategory} onNavigate={handleNavigate} />
      
      <HeroSection onScrollToMenu={scrollToMenu} />
      
      <div id="menu-container">
        <CategoryTabs 
          activeCategory={activeCategory} 
          onCategoryChange={handleNavigate} 
        />
        
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
        
        <KidsMenuHighlight onSelect={() => handleNavigate('kids_menu')} />
        <FoodSafetyNotice />
        
        <MenuSection
          key={activeCategory}
          categoryId={activeCategory}
          searchQuery={searchQuery}
          activeFilter={activeFilter}
          onOpenModal={handleOpenModal}
        />
      </div>
      
      <Footer />
      
      <FloatingButtons />
      
      <CartDrawer />
      
      <ProductModal
        item={selectedProduct}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <CartProvider>
          <ScrollProvider>
            <MenuPage />
          </ScrollProvider>
        </CartProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;